import React from 'react';
import Link from 'next/link';
import { AlertItem, AlertResult, AlertSeverity } from '@/types/dashboard';

interface DashboardAlertsProps {
  alerts: AlertResult | null;
}

function getSeverityBadge(severity: AlertSeverity) {
  switch (severity) {
    case 'CRITICAL':
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-700 border border-rose-200">
          <span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" />
          CRITICAL
        </span>
      );
    case 'WARNING':
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-800 border border-amber-200">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
          WARNING
        </span>
      );
    case 'INFO':
    default:
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700 border border-blue-200">
          <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
          INFO
        </span>
      );
  }
}

function getSeverityBorder(severity: AlertSeverity) {
  switch (severity) {
    case 'CRITICAL':
      return 'border-l-4 border-l-rose-500 bg-rose-50/20';
    case 'WARNING':
      return 'border-l-4 border-l-amber-500 bg-amber-50/20';
    case 'INFO':
    default:
      return 'border-l-4 border-l-blue-500 bg-blue-50/20';
  }
}

export default function DashboardAlerts({ alerts }: DashboardAlertsProps) {
  if (!alerts || !alerts.alerts || alerts.alerts.length === 0) {
    return (
      <div className="rounded-xl bg-white p-5 shadow-xs border border-gray-200/80" data-testid="dashboard-alerts-card">
        <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
          <span>🔔</span>
          <span>Operational Priority Alerts</span>
        </h3>
        <div className="flex items-center gap-3 rounded-xl bg-emerald-50/80 p-4 text-emerald-900 border border-emerald-200">
          <span className="text-xl">✨</span>
          <div>
            <p className="text-xs font-bold">All Channels Nominal</p>
            <p className="text-[11px] text-emerald-700 mt-0.5">Zero operational bottlenecks or settlement disputes detected.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-white p-5 shadow-xs border border-gray-200/80 flex flex-col justify-between" data-testid="dashboard-alerts-card">
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <span>🔔</span>
            <span>Operational Alerts</span>
          </h3>
          <span className="inline-flex items-center rounded-full bg-rose-50 border border-rose-200 px-2.5 py-0.5 text-xs font-extrabold text-rose-700">
            {alerts.total} Active
          </span>
        </div>

        <div className="space-y-2.5 max-h-[380px] overflow-y-auto pr-1">
          {alerts.alerts.map((item: AlertItem, idx: number) => (
            <div
              key={idx}
              className={`p-3 rounded-lg border border-gray-200/70 hover:shadow-xs transition-all flex flex-col justify-between gap-2 ${getSeverityBorder(item.severity)}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    {getSeverityBadge(item.severity)}
                    <span className="text-xs font-bold text-gray-900">{item.title}</span>
                  </div>
                  <p className="text-[11px] text-gray-600 leading-relaxed">{item.message}</p>
                </div>
              </div>

              {item.action && (
                <div className="flex justify-end pt-1 border-t border-gray-100">
                  <Link
                    href={item.action}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
                    data-testid={`alert-action-link-${idx}`}
                  >
                    <span>Resolve Issue</span>
                    <span className="text-xs">➔</span>
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

